import { useEffect } from 'react';
import { Alert, Button, Group, Stack, Table, Text } from '@mantine/core';
import { useConsoleNav } from '../shell/nav';
import { AutoGrid, ChipRail, ConsoleTable, EstatePill, PageHeader, Pill, Row, StatCard, StatSkeleton, TableMessage } from '../ui';
import { num } from '../ui/format';
import { tokens as T } from '../theme/tokens';
import { useEstatesStore, useMemoryTenantsStore, useTenantsStore } from '../stores/dataStores';
import { isUnconfigured, rowsOf } from '../stores/collection';
import { useUIStore } from '../stores/uiStore';
import {
  memoryBrowserPath,
  memoryCapUse,
  memoryTenantRows,
  memoryTenantTotals,
  pickMemoryEstate,
  tenantNavCursor,
  tenantNavFor,
  totalsAreWhole,
} from '../data/memoryState';

const COLS = [
  { key: 'tenant', label: 'Tenant' },
  { key: 'active', label: 'Active', w: 100, align: 'right' as const },
  { key: 'cap', label: 'Cap use', w: 150 },
  { key: 'superseded', label: 'Superseded', w: 110, align: 'right' as const },
  { key: 'deleted', label: 'Forgotten', w: 110, align: 'right' as const },
  { key: 'open', w: 44 },
];

/** The estate overview of long-term memory (DR-86): which tenants of one
 *  estate hold memories, and how many, from one admin route that reads
 *  `router_memories` itself — ids and counts, never a memory. A row opens the
 *  tenant's own browser (DR-75), where the memories are. */
export function MemoryOverviewPage() {
  const navigate = useConsoleNav();
  const ui = useUIStore();
  const estatesCol = useEstatesStore((s) => s.data);
  const estates = rowsOf(estatesCol);
  const registry = rowsOf(useTenantsStore((s) => s.data));
  const tenantsCol = useMemoryTenantsStore((s) => s.data);
  const load = useMemoryTenantsStore((s) => s.load);

  // The estate is derived, not stored: the header's scope, a choice, or the
  // first estate with memory on. The cursor stack is tagged with the estate
  // it pages, so however the estate changes, a cursor never crosses over.
  const estateId = pickMemoryEstate(estates, ui.scope, ui.memoryEstate);
  const nav = tenantNavFor(ui.memoryTenantNav, estateId);
  const cursor = tenantNavCursor(ui.memoryTenantNav, estateId);
  useEffect(() => {
    if (estateId) load({ estateId, limit: ui.memoryTenantPageSize, cursor: cursor || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estateId, ui.memoryTenantPageSize, cursor]);

  const estate = estatesCol.byId[estateId];
  const memoryOn = estate?.memory?.mode === 'on';
  // Only this estate's rows: a load in flight leaves the last estate's rows in
  // the store, and they must not render under this estate's policy.
  const rows = memoryTenantRows(rowsOf(tenantsCol), registry, estateId);
  const totals = memoryTenantTotals(rows);
  const ready = tenantsCol.phase === 'ready';
  const pending = tenantsCol.phase === 'loading' && rows.length === 0;
  const whole = ready && totalsAreWhole(nav.page, tenantsCol.nextCursor);
  const failed = tenantsCol.phase === 'error' && tenantsCol.error;

  const estateOptions = estates.map((e) => ({
    value: e.id,
    label: e.displayName.startsWith(e.id) ? e.displayName : `${e.id} — ${e.displayName}`,
  }));
  const stat = (n: number) => (pending ? <StatSkeleton /> : rows.length || ready ? num(n) : '—');

  return (
    <Stack gap={16}>
      <PageHeader
        title="Agent memory"
        lede="Which tenants of an estate hold long-term memories, and how many. Ids and counts only — open a tenant to read its memories."
      />

      <Group gap={12} wrap="wrap" align="center">
        {ui.scope ? (
          <Text fz={13} c={T.ink3}>
            Scoped to <code>{ui.scope}</code> — showing its estate.
          </Text>
        ) : estateOptions.length ? (
          <ChipRail value={estateId} onChange={(v) => ui.setMemoryEstate(v)} data={estateOptions} />
        ) : null}
        {estateId ? <EstatePill estate={estateId} size="lg" /> : null}
        {estate ? (
          <Text fz={13} c={T.ink3}>
            Memory {memoryOn ? 'on' : 'off'}
            {memoryOn ? ` · cap ${num(memoryCapUse(0, estate.memory?.maxMemoriesPerTenant).cap)} per tenant` : ''}
          </Text>
        ) : null}
      </Group>

      {estate && !memoryOn ? (
        <Alert color="orange" radius="lg" title="Memory is off for this estate">
          Every /v1/memory/* call for {estateId} answers 403. Memories written while it was on are still held; any are listed below. Turn it
          on with <code>haus memory set --estate {estateId} --mode on --apply</code>.
        </Alert>
      ) : null}

      {failed ? (
        <Alert color="red" radius="lg" title="The last load failed">
          {tenantsCol.error}
        </Alert>
      ) : null}

      <AutoGrid min={160} gap={12}>
        <StatCard label="Tenants with memories" value={stat(totals.tenants)} />
        <StatCard label="Active" value={stat(totals.active)} />
        <StatCard label="Superseded" value={stat(totals.superseded)} />
        <StatCard label="Forgotten (tombstones)" value={stat(totals.deleted)} />
      </AutoGrid>
      {ready ? (
        <Text fz={12} c={T.ink4} mt={-6}>
          {whole
            ? `The whole estate: every tenant of ${estateId} that holds a memory, in any status.`
            : `This page's tenants only (page ${nav.page + 1}). An estate total is the sum over every page.`}
        </Text>
      ) : null}

      <ConsoleTable cols={COLS} minWidth={720}>
        {!estateId ? (
          <TableMessage
            span={COLS.length}
            title={isUnconfigured(estatesCol) ? 'Not connected' : 'No estates'}
            detail={isUnconfigured(estatesCol) ? 'The estate registry is not readable from this session.' : 'There is no estate to show memory for.'}
          />
        ) : isUnconfigured(tenantsCol) ? (
          <TableMessage
            span={COLS.length}
            title="Not connected"
            detail="This session has no admin route to read memory counts through. That is not the same as there being none."
          />
        ) : pending ? (
          <TableMessage span={COLS.length} title="Loading…" />
        ) : rows.length === 0 && failed ? (
          <TableMessage span={COLS.length} title="Nothing to show" detail="The load for this estate failed; see above." />
        ) : rows.length === 0 ? (
          <TableMessage
            span={COLS.length}
            title={nav.page > 0 ? 'No more tenants' : `No memories in ${estateId}`}
            detail={nav.page > 0 ? 'The previous page ended at the last tenant.' : 'No tenant of this estate has called /v1/memory/remember.'}
          />
        ) : (
          rows.map((r) => {
            const use = memoryCapUse(r.active, estate?.memory?.maxMemoriesPerTenant);
            return (
              <Row
                key={r.key}
                selected={ui.scope === r.key}
                onClick={() => {
                  ui.openMemoryBrowser();
                  navigate(memoryBrowserPath(r.estateId, r.tenantId));
                }}
              >
                <Table.Td>
                  <Group gap={10} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text fz={13} fw={600} truncate>
                      {r.displayName ?? r.tenantId}
                    </Text>
                    {r.displayName ? (
                      <Text ff="monospace" fz={12} c={T.ink5} truncate>
                        {r.tenantId}
                      </Text>
                    ) : (
                      <Pill tone="neutral" size="sm">
                        not in registry
                      </Pill>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text ff="monospace" fz={13} ta="right">
                    {num(r.active)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {memoryOn ? (
                    <Group gap={8} wrap="nowrap">
                      <Text ff="monospace" fz={12} c={use.level === 'ok' ? T.ink3 : use.level === 'warn' ? T.marigoldFg : T.coralDeep}>
                        {percent(use.fraction)}
                      </Text>
                      {use.level !== 'ok' ? (
                        <Pill tone={use.level === 'full' ? 'fail' : 'warn'} size="sm">
                          {use.level === 'full' ? 'at cap' : 'near cap'}
                        </Pill>
                      ) : null}
                    </Group>
                  ) : (
                    <Text fz={12} c={T.ink5}>
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text ff="monospace" fz={13} c={T.ink3} ta="right">
                    {num(r.superseded)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text ff="monospace" fz={13} c={T.ink3} ta="right">
                    {num(r.deleted)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text c={T.ink4}>→</Text>
                </Table.Td>
              </Row>
            );
          })
        )}
      </ConsoleTable>

      <Group justify="space-between" align="center">
        <Text fz={12} c={T.ink4}>
          Page {nav.page + 1}
        </Text>
        <Group gap={8}>
          <Button variant="default" disabled={nav.page === 0} onClick={() => ui.prevMemoryTenantPage(estateId)}>
            ← Previous
          </Button>
          {/* Only from a page that loaded: a cursor still on screen from a load
              in flight, or from the last estate, opens nothing in this one. */}
          <Button
            variant="default"
            disabled={!ready || !tenantsCol.nextCursor}
            onClick={() => tenantsCol.nextCursor && ui.nextMemoryTenantPage(estateId, tenantsCol.nextCursor)}
          >
            Next →
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

function percent(fraction: number): string {
  const p = fraction * 100;
  return `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
}
